import React from 'react';

import { FeatureFlagged, FeatureGatedVariant } from '../../../feature-gates/feature-flagged';
import { FeatureGates } from '../../../feature-gates/feature-gates';
import { MessagePreviewItem } from '../../../messages/components/message-preview-item';
import { EmailMessage } from '../../../messages/legacy-components/email-message';
import { TextMessage } from '../../../messages/legacy-components/text-message';
import { UiButton } from '../../../ui/ui-button';
import { surveysRoute } from '../../../web-routes';

import { AutomationWarning } from './automation-warning';

import { SDTC_TIME_ZONES } from '../../../employees/utils/start-date-triggered-communication-time-utils';
import { MESSAGE_TYPE } from '../../../util/message-types';
import { START_DATE_TRIGGERED_COMMUNICATION_TYPES } from '../../../util/start-date-triggered-communication-types';
import { VCardMessage } from '../../../widgets/contact-card/v-card-message';
import {
  getMessageBody,
  getMessageType,
  getOtherLanguages,
  getSendTime,
  getSubject,
} from '../../utils/employee-automation-display-utils';
import { isApplicantAutomation } from '../../utils/is-applicant-automation';

type InputProps = {
  automationToRender: StartDateTriggeredCommunication;
  hasSpanishSpeakingRecipients: boolean;
  // Remove and use hasSupervisorsPercent after PROD-4039
  hasMissingSupervisorsIssue: boolean;
  canEditEmployeeAutomations: boolean;
  orgId: number;
  orgName: string;
  missingSupervisorsIssueLink: string;
  toggleEdit: () => void;
  hasSupervisorsPercent?: number;
};

export const EmployeeAutomationComponent = ({
  automationToRender,
  hasSpanishSpeakingRecipients,
  hasMissingSupervisorsIssue,
  canEditEmployeeAutomations,
  orgId,
  orgName,
  missingSupervisorsIssueLink,
  toggleEdit,
  hasSupervisorsPercent,
}: InputProps) => {
  // we indicate to the user that the SDTC should have a Spanish message if:
  //   - the STDC is a message to employees and at least one employee in the
  //     directory speaks Spanish, or
  //   - the STDC is an alert and at least one of the recipients speaks Spanish
  const shouldHaveSpanishMessage = isApplicantAutomation(automationToRender)
    ? false
    : hasSpanishSpeakingRecipients &&
      ((automationToRender.StartDateTriggeredMessage &&
        !automationToRender.StartDateTriggeredMessage.spanishMessageBody) ||
        (automationToRender.StartDateTriggeredAlert &&
          !automationToRender.StartDateTriggeredAlert.spanishMessageBody));

  return (
    <div className="u-margin-top">
      {automationToRender.StartDateTriggeredAlert?.sendToSupervisors &&
        hasMissingSupervisorsIssue && (
          <div className="u-margin-bottom">
            <AutomationWarning
              missingFieldIssue={{
                object: 'employees',
                field: 'supervisors',
                percentCorrect: hasSupervisorsPercent,
              }}
              link={missingSupervisorsIssueLink}
              variant="employeeIssue"
            />
          </div>
        )}
      {automationToRender.StartDateTriggeredMessage?.hasVCardAttached && (
        <VCardMessage orgId={orgId} isScheduledMessage />
      )}
      {shouldHaveSpanishMessage && (
        <div className="typo-subtitle u-margin-bottom">
          Spanish-speaking employees will receive this message in English.{' '}
          <span role="presentation" className="typo-link" onClick={toggleEdit}>
            Fix now
          </span>
        </div>
      )}
      <FeatureFlagged
        variant={FeatureGatedVariant.HIDE_ON_FLAG}
        flagName={FeatureGates.INTEGRATED_MESSAGING}
      >
        {getMessageType(automationToRender) === MESSAGE_TYPE.TEXT ? (
          <TextMessage
            senderName={orgName}
            sendDateTime={getSendTime(automationToRender)}
            timeZone={SDTC_TIME_ZONES[automationToRender.timeZoneId]}
            messageBody={getMessageBody(automationToRender)}
            isAutomated={
              automationToRender.communicationType !==
              START_DATE_TRIGGERED_COMMUNICATION_TYPES.MESSAGE
            }
            otherLanguages={getOtherLanguages(automationToRender)}
            disableOtherLanguagesMessage={!hasSpanishSpeakingRecipients}
          />
        ) : (
          <EmailMessage
            isAutomated={
              automationToRender.communicationType !==
              START_DATE_TRIGGERED_COMMUNICATION_TYPES.MESSAGE
            }
            senderName="Team Engine (automatically)"
            subject={getSubject(automationToRender) || ''}
            messageBody={getMessageBody(automationToRender)}
            sendDateTime={getSendTime(automationToRender)}
            timeZone={SDTC_TIME_ZONES[automationToRender.timeZoneId]}
            otherLanguages={getOtherLanguages(automationToRender)}
            disableOtherLanguagesMessage={!hasSpanishSpeakingRecipients}
          />
        )}
      </FeatureFlagged>
      <FeatureFlagged
        variant={FeatureGatedVariant.SHOW_ON_FLAG}
        flagName={FeatureGates.INTEGRATED_MESSAGING}
      >
        <MessagePreviewItem
          messageType={getMessageType(automationToRender)}
          senderName={
            getMessageType(automationToRender) === MESSAGE_TYPE.TEXT
              ? orgName
              : 'Team Engine (automatically)'
          }
          subject={getSubject(automationToRender) || ''}
          sendDateTime={getSendTime(automationToRender)}
          recipientName={
            automationToRender.communicationType === START_DATE_TRIGGERED_COMMUNICATION_TYPES.ALERT
              ? 'The Supervisor'
              : 'The Employee'
          }
          timeZone={SDTC_TIME_ZONES[automationToRender.timeZoneId]}
          messageBody={getMessageBody(automationToRender)}
          otherLanguages={getOtherLanguages(automationToRender)}
          disableOtherLanguagesMessage={!hasSpanishSpeakingRecipients}
        />
      </FeatureFlagged>
      <div className="u-display-flex u-justifyContent-flexEnd">
        {automationToRender.Survey && (
          <UiButton
            size="sm"
            variant="dismiss"
            linkTo={surveysRoute(
              automationToRender.Survey.orgId,
              automationToRender.Survey.surveyId,
            )}
            target="_blank"
            rel="noopener noreferrer"
          >
            View Survey Results
          </UiButton>
        )}
        {canEditEmployeeAutomations && (
          <UiButton
            size="xs"
            variant="tertiary"
            onClick={toggleEdit}
            className={automationToRender.Survey ? 'u-margin-left' : ''}
            dataCy="edit-employee-automation"
          >
            Edit
          </UiButton>
        )}
      </div>
    </div>
  );
};
